import React, {useState, useEffect} from 'react';
import axios from 'axios';
import {
  TrendingUp,
  DollarSign,
  Calendar,
  CheckCircle,
  AlertCircle,
  Info,
  Zap,
  Sun,
  FileText,
  ArrowRight
} from 'lucide-react';
import './ScenariosProsolElec.css';
import {usePermissions} from '../../utils/usePermissions';

const API_URL = import.meta.env.VITE_API_URL;

const ScenariosProsolElec = ({clientId, dossierId, dossier, onScenarioApplied}) => {
  const {hasPermission} = usePermissions();
  const [scenarios, setScenarios] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showBaremes, setShowBaremes] = useState(false);
  const [showAcompteCancelModal, setShowAcompteCancelModal] = useState(false);
  const [acompteToCancel, setAcompteToCancel] = useState(null);
  const [raisonAnnulation, setRaisonAnnulation] = useState('');
  const [configForm, setConfigForm] = useState({
    nombreMois: 24,
    dateDebutCredit: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    chargerScenarios();
  }, [clientId, dossierId]);

  const chargerScenarios = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/clients/${clientId}/dossiers/${dossierId}/scenarios-prosol-elec`,
        {headers: {Authorization: `Bearer ${token}`}}
      );
      setScenarios(response.data.data);
      setError(null);
    } catch (err) {
      console.error('Erreur lors du chargement des scénarios:', err);
      setError('Impossible de charger les scénarios Prosol Elec');
    } finally {
      setLoading(false);
    }
  };

  const appliquerScenario = async (confirmCancelAcompte = false) => {
    if (!selectedScenario) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const payload = {
        scenario: selectedScenario,
        nombreMois: parseInt(configForm.nombreMois),
        dateDebutCredit: configForm.dateDebutCredit
      };

      if (confirmCancelAcompte) {
        if (!raisonAnnulation.trim()) {
          setError("La raison de l'annulation de l'acompte est obligatoire.");
          setLoading(false);
          return;
        }
        payload.annulerAcompte = true;
        payload.raisonAnnulation = raisonAnnulation.trim();
      }

      const response = await axios.post(
        `${API_URL}/clients/${clientId}/dossiers/${dossierId}/appliquer-scenario-prosol-elec`,
        payload,
        {headers: {Authorization: `Bearer ${token}`}}
      );

      setSuccess(`Scénario appliqué avec succès !`);
      setShowModal(false);
      setShowAcompteCancelModal(false);
      setSelectedScenario(null);
      setRaisonAnnulation('');

      // Notifier le parent
      if (onScenarioApplied) {
        onScenarioApplied(response.data.data);
      }

      // Recharger les scénarios
      setTimeout(() => {
        chargerScenarios();
        setSuccess(null);
      }, 2000);
    } catch (err) {
      console.error('Erreur lors de l\'application du scénario:', err);
      if (err.response?.data?.requiresAcompteCancellation) {
        setAcompteToCancel(err.response.data.acompte);
        setShowAcompteCancelModal(true);
        setError(null);
      } else {
        setError(err.response?.data?.message || 'Erreur lors de l\'application du scénario');
      }
    } finally {
      setLoading(false);
    }
  };

  const ouvrirModalScenario = (scenarioKey) => {
    setSelectedScenario(scenarioKey);
    setShowModal(true);
  };

  const fermerModal = () => {
    setShowModal(false);
    setSelectedScenario(null);
  };

  const formatMontant = (montant) => {
    return new Intl.NumberFormat('fr-TN', {
      style: 'currency',
      currency: 'TND',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(montant);
  };

  const getScenarioColor = (key) => {
    const colors = {
      minimal: '#3b82f6',
      optimal: '#10b981',
      hautRendement: '#f59e0b',
      actuel: '#6366f1'
    };
    return colors[key] || '#6b7280';
  };

  const getScenarioIcon = (key) => {
    const icons = {
      minimal: Zap,
      optimal: CheckCircle,
      hautRendement: Sun,
      actuel: Info
    };
    return icons[key] || Info;
  };

  const normaliserDate = (value) => {
    if (!value) {
      return null;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return date;
  };

  const formatDateCourt = (value) => {
    const date = normaliserDate(value);
    return date ? date.toLocaleDateString('fr-FR') : 'Non renseignée';
  };

  const getEcheanceNonPayeeLaPlusProche = (autofinancement) => {
    if (!autofinancement) {
      return null;
    }

    const aujourdHui = new Date();
    aujourdHui.setHours(0, 0, 0, 0);

    const echeancier = Array.isArray(autofinancement.echeancier) ? autofinancement.echeancier : [];
    let echeanceProche = null;

    echeancier.forEach((echeance) => {
      if (!echeance || echeance.statut === 'paye' || echeance.statut === 'annule' || echeance.statut === 'retard') {
        return;
      }

      const dateEcheance = normaliserDate(echeance.dateEcheance);
      if (!dateEcheance) {
        return;
      }

      const dNorm = new Date(dateEcheance);
      dNorm.setHours(0, 0, 0, 0);
      if (dNorm < aujourdHui) {
        return;
      }

      const montantRestant = echeance.montantRestant !== undefined && echeance.montantRestant !== null
        ? Number(echeance.montantRestant)
        : Math.max(0, (Number(echeance.montant) || 0) - (Number(echeance.montantPaye) || 0));

      if (!echeanceProche || dateEcheance < echeanceProche.date) {
        echeanceProche = {
          date: dateEcheance,
          montant: montantRestant,
          source: 'echeancier'
        };
      }
    });

    const prochaineEcheance = autofinancement.prochaineEcheance ? autofinancement.prochaineEcheance : null;
    const dateProchaine = prochaineEcheance ? normaliserDate(prochaineEcheance.dateEcheance) : null;
    if (dateProchaine) {
      const dProchNorm = new Date(dateProchaine);
      dProchNorm.setHours(0, 0, 0, 0);
      if (dProchNorm >= aujourdHui) {
        const depuisResume = {
          date: dateProchaine,
          montant: Number(prochaineEcheance.montant) || 0,
          source: 'resume'
        };

        if (!echeanceProche || dateProchaine < echeanceProche.date) {
          echeanceProche = depuisResume;
        }
      }
    }

    return echeanceProche;
  };

  const construireSuiviProchainesEtapes = () => {
    if (!dossier) {
      return null;
    }

    const autofinancement = dossier.autofinancement ? dossier.autofinancement : {};
    const typePaiement = dossier.typePaiement ? dossier.typePaiement : 'comptant';

    const montantCreditTotal = Number(autofinancement.montantTotal) || Number(dossier.montantCredit) || 0;
    const montantCreditPaye = Number(autofinancement.montantPaye) || 0;
    const montantCreditRestant = Math.max(0, montantCreditTotal - montantCreditPaye);

    const montantComptantInitial = Number(dossier.montantComptant) || Number(autofinancement.montantComptantInitial) || 0;
    const montantComptantPaye = Number(autofinancement.montantComptantPaye) || 0;
    const montantComptantRestant = Math.max(0, montantComptantInitial - montantComptantPaye);

    const montantStegTotal = Number(dossier.montantSteg) || 0;
    const stegPaye = dossier.stegPaye === true;
    const montantStegRestant = stegPaye ? 0 : montantStegTotal;
    const montantStegPaye = stegPaye ? montantStegTotal : 0;

    const totalAutofinancement = montantCreditTotal + montantComptantInitial + montantStegTotal;
    const totalPaye = montantCreditPaye + montantComptantPaye + montantStegPaye;

    const clientAvecCredit = typePaiement === 'credit' || typePaiement === 'mixte' || montantCreditTotal > 0;

    const etatDossier = dossier.etatDossier ? dossier.etatDossier : '';
    const rangEtat = {
      'Déposé chez Sastra': 1,
      'En attente d\'approbation': 2,
      'En attente d\'installation': 3,
      'En attente de réception': 4,
      'Réceptionné': 5,
      'Manque PV': 5,
      'Dossier en facturation': 6
    };
    const rangActuel = rangEtat[etatDossier] || 0;

    const dateArrivee = normaliserDate(dossier.dateArrivDossier);
    const dateDepotSteg = normaliserDate(dossier.dateDepotSteg);
    const dateDF = normaliserDate(dossier.dateDF);
    const dateInstallation = normaliserDate(dossier.dateLivraison);
    const dateReception = normaliserDate(dossier.dateMiseEnService);

    const prochaineEcheance = getEcheanceNonPayeeLaPlusProche(autofinancement);

    const aujourdHui = new Date();
    aujourdHui.setHours(0, 0, 0, 0);

    let etatProchaineEcheance = 'Aucune échéance planifiée';
    let classeProchaineEcheance = 'spx-next-ech-neutral';
    let texteProchaineEcheance = 'Aucune échéance active';

    if (prochaineEcheance && prochaineEcheance.date) {
      const dateEcheance = new Date(prochaineEcheance.date);
      dateEcheance.setHours(0, 0, 0, 0);

      const diffMs = dateEcheance.getTime() - aujourdHui.getTime();
      const diffJours = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

      texteProchaineEcheance = `${formatDateCourt(prochaineEcheance.date)} - ${formatMontant(prochaineEcheance.montant)}`;

      if (diffJours < 0) {
        etatProchaineEcheance = `En retard de ${Math.abs(diffJours)} jour(s)`;
        classeProchaineEcheance = 'spx-next-ech-late';
      } else if (diffJours <= 7) {
        etatProchaineEcheance = `À échéance dans ${diffJours} jour(s)`;
        classeProchaineEcheance = 'spx-next-ech-soon';
      } else {
        etatProchaineEcheance = `Prévue dans ${diffJours} jour(s)`;
        classeProchaineEcheance = 'spx-next-ech-upcoming';
      }
    }

    let prochaineAction = 'Dossier clôturé';
    let detailProchaineAction = 'Toutes les étapes principales semblent finalisées.';

    if (!dateArrivee && rangActuel < 1) {
      prochaineAction = 'Finaliser les signatures client';
      detailProchaineAction = clientAvecCredit
        ? 'Client crédit: finaliser 4 formulaires et 4 contrats, puis enregistrer la date d\'arrivée dossier.'
        : 'Client comptant: finaliser 4 contrats, puis enregistrer la date d\'arrivée dossier.';
    } else if (!dateDepotSteg && rangActuel < 2) {
      prochaineAction = 'Préparer puis déposer le dossier chez STEG';
      detailProchaineAction = 'Consolider données techniques/administratives (stock, panneaux, onduleur), obtenir la décharge et renseigner la date de dépôt.';
    } else if (!dateDF && rangActuel < 3) {
      prochaineAction = 'Obtenir et payer le DF';
      detailProchaineAction = 'Le paiement DF réserve officiellement le client au nom de Sastra dans le système STEG.';
    } else if (!dateInstallation && rangActuel < 4) {
      prochaineAction = 'Planifier et réaliser l\'installation';
      detailProchaineAction = 'Affecter l\'équipe technique disponible et lancer l\'installation pour passer en attente de réception.';
    } else if (!dateReception && rangActuel < 5) {
      prochaineAction = 'Programmer la réception (pose compteur)';
      detailProchaineAction = 'Informer la STEG de fin d\'installation et fixer la date de réception.';
    } else if (clientAvecCredit) {
      if (rangActuel < 6 && montantCreditRestant > 0) {
        prochaineAction = 'Finaliser parcours crédit Prosol';
        detailProchaineAction = 'Après réception: dépôt STEG Kamel Ataturk, conformité client, puis paiement banque au profit de Sastra.';
      } else if (montantCreditRestant > 0 || montantStegRestant > 0) {
        prochaineAction = 'Suivre le remboursement crédit / STEG';
        detailProchaineAction = 'Continuer le suivi des échéances mensuelles et/ou du crédit STEG jusqu\'au règlement total.';
      }
    } else if (montantComptantRestant > 0 || montantStegRestant > 0) {
      prochaineAction = 'Encaisser le solde restant';
      detailProchaineAction = 'Encaissement du solde comptant et/ou du crédit STEG jusqu\'à paiement final.';
    }

    const etapes = [
      {
        key: 'signature',
        titre: 'Signature client & dépôt Sastra',
        description: clientAvecCredit ? '4 formulaires + 4 contrats signés.' : '4 contrats signés.',
        date: dateArrivee,
        termine: !!dateArrivee || rangActuel >= 1
      },
      {
        key: 'depot-steg',
        titre: 'Préparation dossier & dépôt STEG',
        description: 'Préparation technique/administrative + décharge STEG.',
        date: dateDepotSteg,
        termine: !!dateDepotSteg || rangActuel >= 2
      },
      {
        key: 'df',
        titre: 'DF & réservation STEG',
        description: 'Paiement DF pour réserver officiellement le client.',
        date: dateDF,
        termine: !!dateDF || rangActuel >= 3
      },
      {
        key: 'installation',
        titre: 'Installation technique',
        description: 'Exécution installation puis passage en attente de réception.',
        date: dateInstallation,
        termine: !!dateInstallation || rangActuel >= 4
      },
      {
        key: 'reception',
        titre: 'Réception / pose compteur',
        description: 'Coordination STEG pour pose compteur et validation finale technique.',
        date: dateReception,
        termine: !!dateReception || rangActuel >= 5
      },
      {
        key: 'cloture',
        titre: clientAvecCredit ? 'Clôture crédit Prosol' : 'Clôture comptant / STEG',
        description: clientAvecCredit
          ? 'Dépôt Kamel Ataturk + conformité + paiement banque.'
          : 'Encaissement total comptant et crédit STEG jusqu\'au solde final.',
        date: null,
        termine: clientAvecCredit ? ((rangActuel >= 6 || montantCreditRestant <= 0) && montantStegRestant <= 0) : (montantComptantRestant <= 0 && montantStegRestant <= 0)
      }
    ];

    let etapeCouranteDefinie = false;
    const etapesAvecStatut = etapes.map((etape) => {
      if (etape.termine) {
        return {
          ...etape,
          statut: 'done'
        };
      }

      if (!etapeCouranteDefinie) {
        etapeCouranteDefinie = true;
        return {
          ...etape,
          statut: 'current'
        };
      }

      return {
        ...etape,
        statut: 'pending'
      };
    });

    let classeEtatDossier = 'spx-dossier-state-start';
    if (rangActuel >= 5) {
      classeEtatDossier = 'spx-dossier-state-success';
    } else if (rangActuel >= 3) {
      classeEtatDossier = 'spx-dossier-state-progress';
    }

    return {
      typeClientLabel: clientAvecCredit ? 'Client crédit' : 'Client comptant',
      typeClientDetail: clientAvecCredit ? '4 formulaires + 4 contrats' : '4 contrats',
      etatDossierLabel: etatDossier || 'Non défini',
      classeEtatDossier,
      prochaineAction,
      detailProchaineAction,
      texteProchaineEcheance,
      etatProchaineEcheance,
      classeProchaineEcheance,
      montantCreditTotal,
      montantCreditRestant,
      montantComptantTotal: montantComptantInitial,
      montantComptantRestant,
      montantStegTotal,
      montantStegRestant,
      totalAutofinancement,
      totalPaye,
      etapes: etapesAvecStatut
    };
  };

  const suiviEtapes = construireSuiviProchainesEtapes();

  if (loading && !scenarios) {
    return (
      <div className="scenarios-loading">
        <div className="spinner"></div>
        <p>Chargement des scénarios Prosol Elec...</p>
      </div>
    );
  }

  if (error && !scenarios) {
    return (
      <div className="scenarios-error">
        <AlertCircle size={48} />
        <p>{error}</p>
        <button onClick={chargerScenarios} className="btn-retry">
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="scenarios-prosol-elec">
      <div className="scenarios-header">
        <div className="header-title">
          <Sun size={32} className="header-icon" />
          <div>
            {/* Ancien titre (commenté, ne pas supprimer): Scénarios d'Installation Prosol Elec */}
            {/* Ancienne description (commentée, ne pas supprimer): Installations photovoltaïques > 4 kWc avec autofinancement partiel */}
            <h2>Prochaines étapes du dossier client</h2>
            <p className="header-subtitle">
              Suivi opérationnel: état dossier, prochaine échéance et action recommandée.
            </p>
          </div>
        </div>

        {/* Section Barème Officiel Prosol Elec (commentée, ne pas supprimer) */}
        {false && (
          <button
            className="btn-toggle-baremes"
            onClick={() => setShowBaremes(!showBaremes)}
          >
            <FileText size={18} />
            {showBaremes ? 'Masquer' : 'Afficher'} barème complet
          </button>
        )}
      </div>

      {/* Messages de succès/erreur */}
      {success && (
        <div className="alert alert-success">
          <CheckCircle size={20} />
          {success}
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      {/* Configuration actuelle */}
      {scenarios && scenarios.puissanceActuelle > 0 && (
        <div className="configuration-actuelle">
          <div className="config-header">
            <Info size={20} />
            <h3>Configuration Actuelle</h3>
          </div>
          <div className="config-content">
            <div className="config-item">
              <span className="label">Puissance installée:</span>
              <span className="value">{scenarios.puissanceActuelle} kWc</span>
            </div>
            <div className="config-item">
              <span className="label">Référence STEG:</span>
              <span className="value">{scenarios.referenceSteg}</span>
            </div>
            {suiviEtapes && (
              <>
                <div className="config-item">
                  <span className="label">Montant autofinancement:</span>
                  <span className="value">{formatMontant(suiviEtapes.totalAutofinancement)}</span>
                </div>
                <div className="config-item">
                  <span className="label">Montant payé:</span>
                  <span className="value">{formatMontant(suiviEtapes.totalPaye)}</span>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {suiviEtapes && (
        <div className="spx-workflow-board">
          <div className="spx-workflow-title-row">
            <h3>Parcours Dossier - Prochaines Étapes</h3>
            <p>Logique opérationnelle selon l'état dossier et le type de client.</p>
          </div>

          <div className="spx-workflow-summary-grid">
            <div className="spx-workflow-summary-card">
              <span className="spx-summary-label">Type client</span>
              <span className="spx-summary-value">{suiviEtapes.typeClientLabel}</span>
              <small>{suiviEtapes.typeClientDetail}</small>
            </div>

            <div className="spx-workflow-summary-card">
              <span className="spx-summary-label">État dossier</span>
              <span className={`spx-dossier-state-badge ${suiviEtapes.classeEtatDossier}`}>{suiviEtapes.etatDossierLabel}</span>
              <small>Suivi en temps réel depuis le CRM</small>
            </div>

            <div className="spx-workflow-summary-card">
              <span className="spx-summary-label">Prochaine échéance</span>
              <span className="spx-summary-value">{suiviEtapes.texteProchaineEcheance}</span>
              <small className={`spx-next-ech-state ${suiviEtapes.classeProchaineEcheance}`}>{suiviEtapes.etatProchaineEcheance}</small>
            </div>

            <div className="spx-workflow-summary-card">
              <span className="spx-summary-label">Next Step</span>
              <span className="spx-summary-value">{suiviEtapes.prochaineAction}</span>
              <small>{suiviEtapes.detailProchaineAction}</small>
            </div>
          </div>

          <div className="spx-workflow-finance-strip">
            {suiviEtapes.montantCreditTotal > 0 && (
              <span>Reste crédit: <strong>{formatMontant(suiviEtapes.montantCreditRestant)}</strong></span>
            )}
            {suiviEtapes.montantComptantTotal > 0 && (
              <span>Reste comptant: <strong>{formatMontant(suiviEtapes.montantComptantRestant)}</strong></span>
            )}
            {suiviEtapes.montantStegTotal > 0 && (
              <span>Reste STEG: <strong>{formatMontant(suiviEtapes.montantStegRestant)}</strong></span>
            )}
          </div>

          <div className="spx-workflow-steps-list">
            {suiviEtapes.etapes.map((etape, index) => (
              <div key={etape.key} className={`spx-workflow-step-item spx-workflow-step-${etape.statut}`}>
                <div className="spx-step-index">{index + 1}</div>
                <div className="spx-step-content">
                  <div className="spx-step-header-row">
                    <h4>{etape.titre}</h4>
                    <span className={`spx-step-status-badge spx-step-status-${etape.statut}`}>
                      {etape.statut === 'done' ? 'Terminé' : etape.statut === 'current' ? 'En cours' : 'À faire'}
                    </span>
                  </div>
                  <p>{etape.description}</p>
                  {etape.date && (
                    <small>Date enregistrée: {formatDateCourt(etape.date)}</small>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Section Barème Officiel Prosol Elec - Installations > 4 kWc (commentée, ne pas supprimer) */}
      {false && showBaremes && scenarios?.scenarios?.tousBaremes && (
        <div className="tous-baremes">
          <h3>
            <FileText size={20} />
            Barème Officiel Prosol Elec - Installations &gt; 4 kWc
          </h3>
          <div className="baremes-table">
            <table>
              <thead>
                <tr>
                  <th>Puissance</th>
                  <th>Contrat Prosol</th>
                  <th>Autofinancement</th>
                  <th>TOTAL</th>
                  <th>Mensualités (24 mois)</th>
                </tr>
              </thead>
              <tbody>
                {scenarios.scenarios.tousBaremes.map((bareme) => (
                  <tr key={bareme.puissance}>
                    <td className="td-puissance">{bareme.puissance} kWc</td>
                    <td className="td-montant">{formatMontant(bareme.montantProsol)}</td>
                    <td className="td-montant highlight">{formatMontant(bareme.autofinancement)}</td>
                    <td className="td-total">{formatMontant(bareme.total)}</td>
                    <td className="td-mensuel">
                      {formatMontant(bareme.mensualites.optimal.montantMensuel)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Section Scénarios recommandés (commentée, ne pas supprimer) */}
      {false && (
      <div className="scenarios-grid">
        {scenarios && Object.entries(scenarios.scenarios)
          .filter(([key]) => ['minimal', 'optimal', 'hautRendement', 'actuel'].includes(key))
          .map(([key, scenario]) => {
            const Icon = getScenarioIcon(key);
            const color = getScenarioColor(key);
            const isActuel = key === 'actuel';

            return (
              <div
                key={key}
                className={`scenario-card ${isActuel ? 'card-actuel' : ''}`}
                style={{borderColor: color}}
              >
                <div className="card-header" style={{backgroundColor: `${color}15`}}>
                  <Icon size={32} style={{color}} />
                  <div>
                    <h3 style={{color}}>{scenario.label}</h3>
                    {scenario.description && (
                      <p className="card-description">{scenario.description}</p>
                    )}
                  </div>
                </div>

                <div className="card-body">
                  {/* Caractéristiques principales */}
                  <div className="card-section">
                    <div className="info-row">
                      <Zap size={18} style={{color}} />
                      <span className="info-label">Puissance:</span>
                      <span className="info-value">{scenario.puissance} kWc</span>
                    </div>

                    <div className="info-row">
                      <DollarSign size={18} style={{color}} />
                      <span className="info-label">Contrat Prosol:</span>
                      <span className="info-value">{formatMontant(scenario.montantProsol)}</span>
                    </div>

                    <div className="info-row highlight-row">
                      <DollarSign size={18} style={{color}} />
                      <span className="info-label">Autofinancement:</span>
                      <span className="info-value highlight">{formatMontant(scenario.autofinancement)}</span>
                    </div>

                    <div className="info-row total-row">
                      <span className="info-label">TOTAL:</span>
                      <span className="info-value total">{formatMontant(scenario.total)}</span>
                    </div>
                  </div>

                  {/* Production estimée */}
                  {scenario.estimationProduction && (
                    <div className="card-section">
                      <h4 className="section-title">
                        <Sun size={16} />
                        Production Estimée
                      </h4>
                      <div className="info-row">
                        <span className="info-label">Annuelle:</span>
                        <span className="info-value">{scenario.estimationProduction.annuelle.toLocaleString()} kWh</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Économie/an:</span>
                        <span className="info-value">{formatMontant(scenario.estimationProduction.economieEstimee)}</span>
                      </div>
                    </div>
                  )}

                  {/* Mensualités */}
                  {scenario.mensualites && (
                    <div className="card-section">
                      <h4 className="section-title">
                        <Calendar size={16} />
                        Options de Remboursement
                      </h4>
                      {Object.entries(scenario.mensualites).map(([type, mensu]) => (
                        <div key={type} className="mensualite-option">
                          <div className="mensu-header">
                            <span className="mensu-type">{mensu.description}</span>
                          </div>
                          <div className="mensu-details">
                            <span>{formatMontant(mensu.montantMensuel)}/mois</span>
                            <span className="mensu-duree">× {mensu.nombreMois} mois</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* ROI */}
                  {scenario.roiEstime && (
                    <div className="card-section roi-section">
                      <TrendingUp size={16} />
                      <span>ROI estimé: <strong>{scenario.roiEstime.annees} ans</strong></span>
                    </div>
                  )}

                  {/* Profil client */}
                  {scenario.profil && (
                    <div className="card-section profil-section">
                      <Info size={16} />
                      <p className="profil-text">{scenario.profil}</p>
                    </div>
                  )}

                  {/* Avantages */}
                  {scenario.avantages && scenario.avantages.length > 0 && (
                    <div className="card-section avantages-section">
                      <h4 className="section-title">
                        <CheckCircle size={16} />
                        Avantages
                      </h4>
                      <ul className="avantages-list">
                        {scenario.avantages.map((avantage, idx) => (
                          <li key={idx}>
                            <CheckCircle size={14} />
                            {avantage}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Cohérence */}
                  {scenario.coherence && (
                    <div className={`card-section coherence-section ${scenario.coherence.coherent ? 'coherent' : 'non-coherent'}`}>
                      {scenario.coherence.coherent ? (
                        <CheckCircle size={16} />
                      ) : (
                        <AlertCircle size={16} />
                      )}
                      <span className="coherence-text">{scenario.coherence.message}</span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                {!isActuel && hasPermission('dossiers', 'update') && (
                  <div className="card-footer">
                    <button
                      className="btn-appliquer"
                      style={{backgroundColor: color}}
                      onClick={() => ouvrirModalScenario(key)}
                      disabled={loading}
                    >
                      Appliquer ce scénario
                      <ArrowRight size={18} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
      </div>
      )}

      {/* Modal scénario (commentée avec la section scénarios, ne pas supprimer) */}
      {false && showModal && selectedScenario && scenarios.scenarios[selectedScenario] && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Appliquer le scénario</h3>
              <button className="btn-close" onClick={fermerModal}>×</button>
            </div>

            <div className="modal-body">
              <div className="scenario-resume">
                <h4>{scenarios.scenarios[selectedScenario].label}</h4>
                <div className="resume-item">
                  <span>Puissance:</span>
                  <strong>{scenarios.scenarios[selectedScenario].puissance} kWc</strong>
                </div>
                <div className="resume-item">
                  <span>Autofinancement total:</span>
                  <strong>{formatMontant(scenarios.scenarios[selectedScenario].autofinancement)}</strong>
                </div>
              </div>

              <div className="config-form">
                <div className="form-group">
                  <label htmlFor="nombreMois">Durée de remboursement (mois)</label>
                  <select
                    id="nombreMois"
                    value={configForm.nombreMois}
                    onChange={(e) => setConfigForm({...configForm, nombreMois: e.target.value})}
                  >
                    <option value="12">12 mois (rapide)</option>
                    <option value="24">24 mois (optimal)</option>
                    <option value="36">36 mois (confortable)</option>
                    <option value="48">48 mois (étalé)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="dateDebutCredit">Date de début du crédit</label>
                  <input
                    id="dateDebutCredit"
                    type="date"
                    value={configForm.dateDebutCredit}
                    onChange={(e) => setConfigForm({...configForm, dateDebutCredit: e.target.value})}
                  />
                </div>

                <div className="mensualite-estimee">
                  <span>Mensualité estimée:</span>
                  <strong>
                    {formatMontant(scenarios.scenarios[selectedScenario].autofinancement / parseInt(configForm.nombreMois))}
                  </strong>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={fermerModal}>
                Annuler
              </button>
              <button
                className="btn-confirm"
                onClick={appliquerScenario}
                disabled={loading}
              >
                {loading ? 'Application...' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmation Annulation Acompte */}
      {showAcompteCancelModal && (
        <div className="modal-overlay" style={{ zIndex: 1100 }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header" style={{ background: '#fef2f2', borderBottom: '1px solid #fee2e2' }}>
              <h3 style={{ color: '#991b1b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <AlertCircle size={20} color="#dc2626" />
                Confirmation d'annulation de l'acompte
              </h3>
              <button className="btn-close" onClick={() => setShowAcompteCancelModal(false)}>×</button>
            </div>

            <div className="modal-body">
              <div style={{ background: '#fff5f5', border: '1px solid #fed7d7', borderRadius: '8px', padding: '12px', marginBottom: '16px', color: '#9b2c2c' }}>
                <p style={{ margin: 0, fontWeight: 'bold' }}>Un acompte actif existe sur ce dossier :</p>
                <p style={{ margin: '4px 0 0 0', fontSize: '14px' }}>
                  Montant : <strong>{acompteToCancel ? formatMontant(acompteToCancel.montant) : ''}</strong>
                </p>
              </div>

              <p style={{ fontSize: '14px', color: '#4a5568', marginBottom: '12px' }}>
                Pour appliquer ce scénario, vous devez obligatoirement confirmer l'annulation de cet acompte et indiquer une raison traçable.
              </p>

              <div className="form-group">
                <label htmlFor="raisonAnnulationAcompte" style={{ fontWeight: 600, fontSize: '13px' }}>
                  Raison de l'annulation <span style={{ color: '#e53e3e' }}>*</span>
                </label>
                <input
                  id="raisonAnnulationAcompte"
                  type="text"
                  placeholder="Ex: Programmation de l'échéancier global avec scénario Prosol..."
                  value={raisonAnnulation}
                  onChange={(e) => setRaisonAnnulation(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e0', marginTop: '4px' }}
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowAcompteCancelModal(false)}>
                Annuler
              </button>
              <button
                className="btn-confirm"
                style={{ backgroundColor: '#dc2626' }}
                onClick={() => appliquerScenario(true)}
                disabled={loading || !raisonAnnulation.trim()}
              >
                {loading ? 'Traitement...' : 'Confirmer et annuler l\'acompte'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScenariosProsolElec;
